/**
 * "Not this resident's" (document correction, 2026-10-14): one inline act shared by the UMED
 * Documents list and the PA Documents tab, so both shells say the same words.
 *
 *   NotThisResidents  the small button and its inline form: a reason (required), optionally
 *                     whose document it really is, picked by search.
 *   MisfiledNote      what a list row says under a struck-through file name.
 *
 * The component holds no API of its own. The shell injects `onSubmit` and `search`, so the
 * UMED and PA routes (which differ) stay in their own api files, and the shells carry only
 * the wiring. The row is never deleted: eMerge keeps the file and the record of the mark.
 */
import React, { useEffect, useRef, useState } from 'react';
import './NotThisResidents.css';

export interface ResidentOption { id: string; name: string; facility_name?: string | null }
export interface MarkResult {
  success: boolean;
  // Only what this form reads. No index signature: an API's named result interface (umedApi.NotThisResidentsMarked) must
  // be assignable as it is (the production build's type check refused it, #416 round 3).
  data?: { message?: string | null } | null;
  error?: string; message?: string; code?: string;
  /** The HTTP status; absent when the server was never reached (services/api.ts: apiRequest resolves, never throws). */
  status?: number;
}
export interface Misfiled { reason: string; at: string; by_name: string | null; outcome?: string; sent?: string | null }
export interface Refiled { at: string; by_name: string | null; mark_undone_at?: string | null }
/** Ruling R3: a mark on this row that a PA manager (or, since 2026-10-28, the PA who made it) undid. */
export interface Undone { at: string; reason: string; by_name: string | null }

/*
 * PR #420 verification (rounds 3-4): when the server's answer is lost, the act may have been saved. Never "nothing was
 * changed": say it is unknown and to look before trying again. The form keeps its key, so a retry replays the act.
 * apiRequest RESOLVES a failure (it never throws): no status means the server was never reached, and a 5xx may have
 * come after the commit (a 502/504 from the proxy) -- both are unknown outcomes, not refusals.
 */
export const UNKNOWN_OUTCOME = "eMerge could not confirm whether this was saved. Check the document's history before trying again.";
export const outcomeUnknown = (r: { status?: number }): boolean => r.status === undefined || r.status >= 500;

const newKey = (): string => (typeof crypto !== 'undefined' && 'randomUUID' in crypto
  ? crypto.randomUUID()
  : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

/** MM/DD/YYYY. A date-only value (or midnight UTC of one) is read as that calendar day; a moment as the viewer's day. */
export function mdy(value: string | null | undefined): string {
  if (!value) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:T00:00:00(?:\.0+)?Z)?$/.exec(value);
  if (m) return `${m[2]}/${m[3]}/${m[1]}`;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
}

/** What a misfiled or refiled document says about itself, under its (struck-through) name. */
export function MisfiledNote({ misfiled, refiled, undone, tone = 'um' }: { misfiled?: Misfiled | null; refiled?: Refiled | null; undone?: Undone | null; tone?: 'um' | 'pa' }) {
  if (!misfiled && !refiled && !undone) return null;
  return (
    <span className={`ntr-note ntr-${tone}`}>
      {misfiled && (
        <>
          <span className="ntr-misfiled">{`Not this resident's (${[misfiled.reason, misfiled.by_name, mdy(misfiled.at)].filter(Boolean).join(', ')})`}</span>
          {misfiled.sent && <span className="ntr-sent">{misfiled.sent}</span>}
        </>
      )}
      {refiled && <span className="ntr-refiled">{`Filed here from another resident (${[refiled.by_name, mdy(refiled.at)].filter(Boolean).join(', ')})`}</span>}
      {refiled && refiled.mark_undone_at && <span className="ntr-sent">{`The mark that filed it here was undone on ${mdy(refiled.mark_undone_at)}. Check whose it is: while both records hold this file, neither packet is sent.`}</span>}
      {undone && !misfiled && <span className="ntr-undone">{`"Not this resident's" was undone (${[undone.reason, undone.by_name, mdy(undone.at)].filter(Boolean).join(', ')}): it is this resident's again.`}</span>}
    </span>
  );
}

/**
 * Ruling R3 (2026-10-14): a PA manager undoes a mark; ruling 2026-10-28: so may the PA who made it, their own, within
 * 24 hours (the shell shows this only where the server's `can_undo` says so, and the server decides again). A reason
 * is required; the undo is recorded, never the mark edited; anything already sent to DCF stays sent (the server's
 * answer says so). A refusal is shown in the server's own words.
 */
export function UndoNotThisResidents({ onSubmit, onDone, tone = 'pa' }: {
  onSubmit: (body: { reason: string; idempotencyKey: string }) => Promise<MarkResult>;
  onDone: (message?: string) => void;
  tone?: 'um' | 'pa';
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const key = useRef<string>(newKey());
  const trimmed = reason.trim();
  if (!open) {
    return <button type="button" className={`${tone}-docbtn ntr-open`} onClick={() => { setOpen(true); setErr(null); key.current = newKey(); }}>Undo &ldquo;Not this resident&rsquo;s&rdquo;</button>;
  }
  const submit = async () => {
    if (trimmed.length < 3 || trimmed.length > 500) { setErr('Say why the mark was wrong (3 to 500 characters).'); return; }
    setBusy(true); setErr(null);
    try {
      const r = await onSubmit({ reason: trimmed, idempotencyKey: key.current });
      if (!r.success) { setErr(outcomeUnknown(r) ? UNKNOWN_OUTCOME : (r.message || r.error || 'The server refused the undo. Nothing was changed.')); return; }
      setOpen(false); setReason('');
      onDone((r.data && r.data.message) || 'The mark was undone.');
    } catch { setErr(UNKNOWN_OUTCOME); } finally { setBusy(false); }
  };
  return (
    <form className={`${tone}-form ntr-form`} aria-label="Undo Not this resident's" onSubmit={(e) => { e.preventDefault(); if (!busy) void submit(); }}>
      <label><span>Why the mark was wrong (required)</span>
        <textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} rows={3} aria-label="Why the mark was wrong" /></label>
      <p className="ntr-sent">The document becomes this resident&rsquo;s again. Anything already sent to DCF stays sent: undoing never un-sends.</p>
      <div className="btns">
        <button type="submit" className={`${tone}-open ${tone}-primary`} disabled={busy}>{busy ? 'Undoing…' : 'Undo the mark'}</button>
        <button type="button" className={`${tone}-open`} disabled={busy} onClick={() => { setOpen(false); setReason(''); setErr(null); }}>Cancel</button>
      </div>
      {err && <p className={`${tone}-notice stop`} role="alert">{err}</p>}
    </form>
  );
}

export default function NotThisResidents({ onSubmit, search, onDone, tone = 'um' }: {
  onSubmit: (body: { reason: string; correctPatientId: string | null; idempotencyKey: string }) => Promise<MarkResult>;
  search?: (q: string) => Promise<ResidentOption[]>;
  onDone: (message?: string) => void;
  tone?: 'um' | 'pa';
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<ResidentOption[]>([]);
  const [picked, setPicked] = useState<ResidentOption | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // One key per form opening: a retry of the same submit replays instead of marking twice.
  const key = useRef<string>(newKey());
  const seq = useRef(0);
  const searchRef = useRef(search);
  searchRef.current = search;
  const canSearch = Boolean(search);

  useEffect(() => {
    const term = q.trim();
    if (!canSearch || picked || term.length < 2) { setHits([]); return undefined; }
    const mine = ++seq.current;
    const t = setTimeout(async () => {
      try {
        const found = await (searchRef.current as (q: string) => Promise<ResidentOption[]>)(term);
        if (mine === seq.current) setHits(Array.isArray(found) ? found : []);
      } catch { if (mine === seq.current) setHits([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [q, picked, canSearch]);

  const close = () => { setOpen(false); setReason(''); setQ(''); setHits([]); setPicked(null); setErr(null); key.current = newKey(); };
  const valid = reason.trim().length >= 3 && reason.trim().length <= 500;

  if (!open) {
    return <button type="button" className={`${tone}-docbtn ntr-open`} onClick={() => setOpen(true)}>{"Not this resident's"}</button>;
  }
  return (
    <form className={`${tone}-form ntr-form`} aria-label="Not this resident's" onSubmit={async (e) => {
      e.preventDefault(); if (!valid || busy) return; setBusy(true); setErr(null);
      try {
        const r = await onSubmit({ reason: reason.trim(), correctPatientId: picked ? picked.id : null, idempotencyKey: key.current });
        if (!r.success) { setErr(outcomeUnknown(r) ? UNKNOWN_OUTCOME : (r.error || r.message || (r.code ? `Refused (${r.code}).` : 'That did not go through.'))); return; }
        const message = r.data && typeof r.data.message === 'string' && r.data.message ? r.data.message : undefined;
        close();
        onDone(message);
      } catch { setErr(UNKNOWN_OUTCOME); } finally { setBusy(false); }
    }}>
      <label><span>{"Why is this not this resident's? (required)"}</span>
        <textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} rows={3} aria-label="Reason" />
      </label>
      {search && (
        <div className="ntr-pick">
          {picked ? (
            <p className="ntr-picked">
              <span>Whose is it: {picked.name}{picked.facility_name ? `, ${picked.facility_name}` : ''}</span>{' '}
              <button type="button" className={`${tone}-docbtn`} onClick={() => { setPicked(null); setQ(''); }}>Clear</button>
            </p>
          ) : (
            <>
              <label><span>Whose is it? (optional)</span>
                <input type="text" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Whose is it?" autoComplete="off" placeholder="Type at least two letters of the name" />
              </label>
              {hits.length > 0 && (
                <ul className="ntr-hits" aria-label="Matching residents">
                  {hits.map((h) => (
                    <li key={h.id}>
                      <button type="button" className="ntr-hit" onClick={() => { setPicked(h); setHits([]); }}>
                        {h.name}{h.facility_name ? ` · ${h.facility_name}` : ''}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      )}
      <div className="btns">
        <button type="submit" className={`${tone}-open ${tone}-primary`} disabled={busy || !valid}>{busy ? 'Recording…' : "Mark not this resident's"}</button>
        <button type="button" className={`${tone}-open`} onClick={close} disabled={busy}>Cancel</button>
      </div>
      {err && <p className={`${tone}-notice stop`} role="alert">{err}</p>}
    </form>
  );
}
